import { useCallback, useEffect, useState } from 'react';
import { logChatPromiseExecution } from 'stream-chat';
import {
  defaultPinPermissions,
  MessageInput,
  MessageList,
  MessageToSend,
  PinEnabledUserRoles,
  Thread,
  useChannelStateContext,
  Window,
} from 'stream-chat-react';

import { PinnedMessageList } from './PinnedMessageList/PinnedMessageList';
import { ChatChannelHeader } from './ChatChannelHeader/ChatChannelHeader';

import { StreamChatType } from './types';
import { useGiphyInMessageContext } from './context/GiphyInMessageFlagContext';
import { ChatMessageInput } from './ChatMessageInput/ChatMessageInput';
import { QuotedMessageProvider, useQuotedMessage } from './context/QuotedMessageContext';
import { QuotedMessagePreview } from './components/QuotedMessagePreview';
import CommentsDisabledIcon from '@mui/icons-material/CommentsDisabled';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../redux/store';
import { ActionCardAPIHandler } from '../services/NetworkClient/BackendAPIs/ActionCardAPIHandler';
import { resetCardMap, setCardMap } from '../redux/actionCard/paymentCard/paymentCardsByChannelSlice';
import { Box, CircularProgress } from '@mui/material';
import { ErrorLogger } from '../utils/Logger';

interface ChannelInnerProps {
  onBackClick?: () => void;
  isMobile?: boolean;
}

const ChannelInnerContent = ({ onBackClick, isMobile }: ChannelInnerProps = {}) => {
  // redux
  const { inputHasGiphyMessage, clearGiphyFlag } = useGiphyInMessageContext();
  const { channel } = useChannelStateContext<StreamChatType>();
  const [loading, setLoading] = useState(true);
  const dispatch = useDispatch();
  const { quotedMessage, clearQuotedMessage } = useQuotedMessage();

  // todo: migrate to channel capabilities once migration guide is available
  const teamPermissions: PinEnabledUserRoles = { ...defaultPinPermissions.team, user: true };
  const messagingPermissions: PinEnabledUserRoles = {
    ...defaultPinPermissions.messaging,
    user: true,
  };

  const pinnedPermissions = {
    ...defaultPinPermissions,
    team: teamPermissions,
    messaging: messagingPermissions,
  };

  const overrideSubmitHandler = useCallback(async (message: MessageToSend) => {
    let updatedMessage: any = {
      attachments: message.attachments,
      mentioned_users: message.mentioned_users,
      text: message.text,
    };

    // Add quoted message if replying (WhatsApp-style)
    // Stream Chat automatically populates quoted_message when you provide quoted_message_id
    if (quotedMessage) {
      updatedMessage.quoted_message_id = quotedMessage.id;
    }

    const isReply = !!message.parent?.id;

    if (inputHasGiphyMessage(isReply)) {
      const updatedText = `/giphy ${message.text}`;
      updatedMessage = { ...updatedMessage, text: updatedText };
    }

    // Use channel.sendMessage directly for better control
    try {
      if (channel) {
        const sendMessagePromise = channel.sendMessage(updatedMessage);
        logChatPromiseExecution(sendMessagePromise, 'send message');
        await sendMessagePromise;
        clearGiphyFlag(isReply);
        // Clear quoted message after sending
        if (quotedMessage) {
          clearQuotedMessage();
        }
      }
    } catch (error) {
      ErrorLogger.log(error, 'ChannelInner.overrideSubmitHandler');
    }
  }, [inputHasGiphyMessage, channel, clearGiphyFlag, quotedMessage, clearQuotedMessage]);

  const isChatLocked = useSelector(
    (state: RootState) => state.chatLock.lockedChannels[channel.id ?? ""] === true
  );

  const ChatBoxLocked = () => (
    <div className='chatBoxLocked-wrapper'>
      <div className='chatBoxLocked-text'>
        ----- &nbsp; <CommentsDisabledIcon /> <Box width={"10px"}></Box> Chat Locked &nbsp; -----
      </div>
    </div>
  )

  const getChannelCards = async () => {
    if (channel.id) {
      setLoading(true)
      const cardsList = await ActionCardAPIHandler.shared.getActionCardsByChannelId(channel.id);
      if (cardsList) {
        dispatch(setCardMap(cardsList));
      }
      setLoading(false)
    }
  }


  useEffect(() => {
    dispatch(resetCardMap());
    getChannelCards();
    // eslint-disable-next-line
  }, [])

  return (
    <>
      <Window>
        <ChatChannelHeader onBackClick={onBackClick} isMobile={isMobile} />
        {loading ?
          <Box height={"100%"} width={"100%"} className="center"> <CircularProgress size={64} thickness={5} /> </Box> :
          <MessageList 
            disableQuotedMessages={false} 
            pinPermissions={pinnedPermissions}
          />}
        {!isChatLocked ? (
          <>
            <QuotedMessagePreview />
            <MessageInput grow overrideSubmitHandler={overrideSubmitHandler} />
          </>
        ) : <ChatBoxLocked />}
      </Window>
      <Thread additionalMessageInputProps={{ grow: true, Input: ChatMessageInput, overrideSubmitHandler }} />
      <PinnedMessageList />
    </>
  );
};

export const ChannelInner = (props: ChannelInnerProps) => {
  return (
    <QuotedMessageProvider>
      <ChannelInnerContent {...props} />
    </QuotedMessageProvider>
  );
};
